import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { clampZoom, zoomedWidth } from './imageZoom';

/**
 * An image in the Files viewer that can be zoomed (OL-241). The zoom is owned
 * by the viewer, so it carries over when stepping to the next image.
 * Ctrl + wheel (or a touchpad pinch) zooms; when zoomed in, drag to move around.
 */
const ImageViewer = ({ src, alt, zoom, onZoomChange, canDownload }) => {
  const boxRef = useRef(null);
  const [box, setBox] = useState(null);
  const [natural, setNatural] = useState(null);
  const drag = useRef(null);

  // The latest values for the native wheel listener, which is added once.
  const latest = useRef({ zoom, onZoomChange });
  latest.current = { zoom, onZoomChange };

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    // React's onWheel is passive, so it can't stop the page zooming instead.
    const onWheel = (event) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      const next = clampZoom(latest.current.zoom * Math.exp(-event.deltaY * 0.002));
      latest.current.onZoomChange(Math.abs(next - 1) < 0.02 ? 1 : Math.round(next * 100) / 100);
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      observer.disconnect();
      el.removeEventListener('wheel', onWheel);
    };
  }, []);

  // The size belongs to the image it was read from. Until the next image loads,
  // the CSS fitting below draws it.
  const size = natural?.src === src ? natural : null;
  const width = zoomedWidth({
    naturalWidth: size?.width,
    naturalHeight: size?.height,
    boxWidth: box?.width,
    boxHeight: box?.height,
    zoom,
  });
  const zoomed = zoom > 1;

  const startDrag = (event) => {
    if (!zoomed || event.button !== 0) return;
    const el = boxRef.current;
    drag.current = { x: event.clientX, y: event.clientY, left: el.scrollLeft, top: el.scrollTop };
    el.setPointerCapture?.(event.pointerId);
  };
  const moveDrag = (event) => {
    if (!drag.current) return;
    const el = boxRef.current;
    el.scrollLeft = drag.current.left - (event.clientX - drag.current.x);
    el.scrollTop = drag.current.top - (event.clientY - drag.current.y);
  };
  const endDrag = () => {
    drag.current = null;
  };

  return (
    <div
      ref={boxRef}
      className={clsx('w-full h-full overflow-auto flex', zoomed && 'cursor-grab active:cursor-grabbing select-none')}
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <img
        src={src}
        alt={alt}
        // m-auto centres the image yet lets an enlarged one scroll to every edge.
        className={clsx('m-auto', width ? 'max-w-none max-h-none' : 'max-w-full max-h-full object-contain')}
        style={width ? { width: `${width}px` } : undefined}
        draggable={canDownload && !zoomed}
        onContextMenu={canDownload ? undefined : (event) => event.preventDefault()}
        onLoad={(event) =>
          setNatural({ src, width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })
        }
      />
    </div>
  );
};

ImageViewer.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  zoom: PropTypes.number.isRequired,
  onZoomChange: PropTypes.func.isRequired,
  canDownload: PropTypes.bool,
};

export { ImageViewer };
